import { useState, useEffect, type FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import OrderReviewWithHash from './OrderReviewWithHash';
import RegisterLayout from '@/registration/component/Register/Layout';
import Header from '@/registration/component/Register/Layout/Header';
import BaseMessageBarError from '@/base/MessageBar/Error';
import InfoBox from '@/registration/component/InfoBox';
import { UiStack, UiHStack, UiButton } from '@/lib/ui';
import OutstandingPaymentForm from './OutstandingPaymentForm';
import { useUserSession } from '@/registration/hook/useUserSession';
import { login, orderQueryKey, type Order, loadOrder } from '@/api/registration';
import { type ApiResponseSingle } from '@/api/tenantClient';

import { useTenantApi } from '@/account/hook/useTenantApi';
import { SkeletonText } from '@chakra-ui/react';

const RegistrationRegisterOutstandingPaymentPage: FC = () => {
  const [searchParams] = useSearchParams();
  const orderHash = searchParams.get('orderHash');
  const token = searchParams.get('token');
  const client = searchParams.get('client');
  const uid = searchParams.get('uid');

  const [errors, setErrors] = useState<string[]>([]);
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);
  const [showPayButton, setShowPayButton] = useState<boolean>(true);
  const { setUserSession, userSession } = useUserSession();
  const { createTenantApiRequest } = useTenantApi();

  const { mutate: loginMutate, isLoading: isLoginLoading } = useMutation(login(createTenantApiRequest), {
    onSuccess: (data) => {
      if (data.item) {
        setUserSession({ email: data.item.uid, client: data.item.client, authToken: data.item.token });
      } else if (data.errors) {
        setErrors(data.errors);
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to authenticate user.']);
    }
  });

  const { data, isLoading, error: fetchError } = useQuery<ApiResponseSingle<Order>, Error>({
    queryKey: [orderQueryKey, { orderHash }],
    queryFn: async () => {
      if (!orderHash) {
        throw new Error('orderHash is missing, cannot complete query');
      }
      return await loadOrder(createTenantApiRequest)(orderHash);
    },
    enabled: !!orderHash && !!userSession
  });

  const handlePayByCreditCard = () => {
    setShowPaymentForm(true);
    setShowPayButton(false);
  }

  useEffect(() => {
    if (token && client && uid) {
      loginMutate({ token, client, uid });
    }
  }, [token, client, uid, loginMutate]);

  useEffect(
    () => {
      const paymentOption = data?.item?.paymentOption;
      if (!paymentOption) {
        return;
      }

      const isInvoice = paymentOption === 'invoice';
      setShowPayButton(isInvoice);
      setShowPaymentForm(!isInvoice);
    },
    [isLoading, data]
  );

  if (isLoginLoading) {
    return (
      <RegisterLayout header={<Header />}>
        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
      </RegisterLayout>
    );
  }

  return (
    <RegisterLayout header={<Header />}>
      <UiStack flexGrow={1} spacing={8} pb={16}>
        <InfoBox
          title={'Outstanding Payment'}
        />
        {orderHash ? (
          <>
            <OrderReviewWithHash orderHash={orderHash} userSession={userSession} />
            {showPayButton && (
              <UiHStack justifyContent={'flex-center'} flexGrow={1}>
                <UiButton
                  px={8}
                  size={'lg'}
                  shadow={'base'}
                  colorScheme={'primary'}
                  onClick={ handlePayByCreditCard }
                  isDisabled={isLoading}
                >
                  Pay by credit card
                </UiButton>
              </UiHStack>
            )}
            {showPaymentForm && (
              <OutstandingPaymentForm orderHash={orderHash} />
            )}
          </>
        ) : (
          <BaseMessageBarError>
            Order not found, please request a new payment link from the event organiser.
          </BaseMessageBarError>
        )}
        {errors.length > 0 && (
          <BaseMessageBarError>
            {errors.join('. ')}
          </BaseMessageBarError>
        )}
      </UiStack>
    </RegisterLayout>
  );
};

export default RegistrationRegisterOutstandingPaymentPage;
